import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArrowRight, Award, Shield, Target, Users, User, Laptop, CheckCircle, BarChart3, FileText } from 'lucide-react';

const impactASPlusLogo = "/opt/brand/impactas-final-2.webp";

const coachingDepthBenefits = [
  "Match coach capability to leader seniority and context",
  "Support large-scale programmes without compromising quality",
  "Provide consistency, supervision, and ethical governance across engagements"
];

const afriExecutiveBenefits = [
  "Dedicated executive and C-suite coaching",
  "Highly experienced MCC-level coaches, the only offering of its kind in West Africa",
  "A discreet, senior-led coaching environment appropriate for top leadership roles"
];

const insightLedBenefits = [
  "Coaching is targeted, not generic",
  "Development priorities are evidence-based",
  "Leaders understand why change is required",
  "Organisations can track patterns and progress over time"
];

const individualCoachingSupports = [
  "Senior leaders and executives",
  "Role transitions or expanded scope",
  "Behavioural shifts identified through diagnostics",
  "Leadership effectiveness under pressure"
];

const individualCoachingIs = [
  "Confidential at the individual level",
  "Governed at the organisational level",
  "Aligned with clearly defined development objectives"
];

const teamCoachingSupports = [
  "Executive teams and senior leadership groups",
  "Leadership alignment and decision effectiveness",
  "Collective behaviour, trust, and accountability",
  "Complex stakeholder and governance environments"
];

const teamCoachingInformedBy = [
  "Team-level diagnostics",
  "Leadership style interactions",
  "Organisational context and expectations"
];

const programmeAttributes = [
  "Cohort-based",
  "Insight-led",
  "Contextually grounded",
  "Linked directly to organisational priorities"
];

const programmeFocus = [
  "Emerging and mid-level leaders",
  "Senior leadership populations",
  "Succession and future leadership pipelines",
  "Specific leadership capabilities or behaviours"
];

const portalFeatures = [
  "Secure, native video conferencing",
  "Session scheduling and management",
  "Action and development tracking",
  "Integrated insight and reflection tools",
  "Programme-level oversight and reporting"
];

const portalEnables = [
  "Run coaching programmes efficiently",
  "Maintain governance and visibility",
  "Support leaders consistently across locations",
  "Integrate coaching with broader leadership initiatives"
];

const insightSources = [
  "RESILIGENT proprietary diagnostics (including DISC Lens Africa™, Leadership Mirror™, and CulturePulse™)",
  "Impact AS Plus™ integrated insight reports",
  "Select external assessments where additional perspective is required"
];

const organisationGains = [
  "Leaders with greater self-awareness and behavioural range",
  "Improved leadership consistency and decision quality",
  "Alignment between leadership behaviour and organisational culture",
  "Reduced reliance on intuition and anecdote",
  "Scalable, governed leadership development infrastructure"
];

const typicalBeginnings = [
  "Coaching following a diagnostic or 360° programme",
  "A leadership cohort informed by insight",
  "Targeted support for specific leaders or teams"
];

const CoachingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Executive Coaching Nigeria | ICF MCC Certified Coaches Lagos"
        description="Best executive coaching in Nigeria with ICF MCC certified coaches. Leadership coaching for CEOs, executives, and senior managers in Lagos and across Africa. One of West Africa's largest MCC-led coaching faculties. Transform your leadership effectiveness."
        keywords="executive coaching Nigeria, ICF certified coach Lagos, leadership coaching Nigeria, CEO coach Nigeria, executive coach Lagos, MCC coach Africa, senior leadership coaching Nigeria, C-suite coaching Lagos, professional coaching Nigeria, business coaching Lagos"
        canonical="/coaching"
      />
      <Header />
      
      <main>
        {/* Hero Section with Background Image */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.9) 0%, rgba(10, 10, 10, 0.75) 40%, rgba(10, 10, 10, 0.4) 100%), url('/opt/photo/ext/u1759310610552-914069ec2e0b.webp')`
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              <h1 className="text-white font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-8">
                Coaching & Leadership Development
              </h1>
              
              <p className="text-[#2693d0] text-lg lg:text-xl font-medium leading-relaxed mb-6">
                Insight-led coaching and leadership development at scale
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-6">
                RESILIGENT delivers coaching and leadership development as leadership infrastructure, grounded in diagnostic insight, delivered by a deep bench of accredited coaches, and supported by proprietary technology.
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg xl:text-xl leading-relaxed max-w-2xl">
                Our work is designed to help organisations develop leaders who can think clearly, act deliberately, and lead effectively in complex environments.
              </p>
            </div>
          </div>
        </section>

        {/* What Distinguishes Our Approach */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mb-16">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                What Distinguishes Our Approach
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-4">
                Coaching and leadership development vary widely in quality and impact.
              </p>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                RESILIGENT's approach is deliberately structured around credibility, depth, and governance.
              </p>
              <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed">
                Three elements distinguish our work.
              </p>
            </div>
          </div>
        </section>

        {/* 1. Unmatched Coaching Depth */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#2693d0]/10 flex items-center justify-center">
                    <Award className="w-6 h-6 text-[#2693d0]" />
                  </div>
                  <span className="text-[#2693d0] text-sm font-semibold tracking-wide">01</span>
                </div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Unmatched Coaching Depth in West Africa
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  RESILIGENT is the only organisation in West Africa with a coaching faculty that includes four International Coaching Federation (ICF) Master Certified Coaches (MCCs), supported by a robust bench of ACC-, PCC-, and MCC-accredited coaches.
                </p>
              </div>
              
              <div>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  This allows us to:
                </p>
                <ul className="space-y-4 mb-8">
                  {coachingDepthBenefits.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#2693d0] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-white rounded-xl p-6 card-pulse-border">
                  <p className="text-slate-700 text-base leading-relaxed">
                    Our coaching faculty operates under clear standards, supervision, and quality assurance, not ad hoc matching.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Executive Coaching - AfriExecutive */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-[#2693d0]" />
                  </div>
                  <span className="text-[#2693d0] text-sm font-semibold tracking-wide">02</span>
                </div>
                <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Executive Coaching for the C-Suite
                </h2>
                <p className="text-slate-200 text-lg font-medium mb-6">
                  AfriExecutive Coaching
                </p>
                <p className="text-slate-300 text-base lg:text-lg leading-relaxed">
                  For senior executives and board-level leaders, RESILIGENT works through its sister company, AfriExecutive Coaching.
                </p>
              </div>
              
              <div>
                <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-6">
                  AfriExecutive Coaching provides:
                </p>
                <ul className="space-y-4 mb-8">
                  {afriExecutiveBenefits.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                      <span className="text-slate-200 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <p className="text-slate-300 text-base leading-relaxed">
                    This separation ensures that executive coaching remains distinct, confidential, and appropriately positioned, while benefiting from RESILIGENT's broader diagnostic and organisational insight.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Insight-led Development */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#2693d0]/10 flex items-center justify-center">
                    <Target className="w-6 h-6 text-[#2693d0]" />
                  </div>
                  <span className="text-[#2693d0] text-sm font-semibold tracking-wide">03</span>
                </div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Insight-Led Development, Not Generic Coaching
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  All coaching and leadership development engagements are informed by diagnostic insight, most commonly through Impact AS Plus™.
                </p>
              </div>
              
              <div>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  Core diagnostic inputs into coaching include:
                </p>
                <div className="space-y-3 mb-6">
                  <a href="/disc-africa" className="flex items-center gap-3 bg-[#f0f5f8] rounded-lg p-4 border border-slate-100 hover:border-[#2693d0] transition-colors group">
                    <div className="w-2 h-2 bg-[#2693d0] rounded-full flex-shrink-0" />
                    <span className="text-slate-700 text-base font-medium group-hover:text-[#2693d0]">DISC Lens Africa™</span>
                    <span className="text-slate-500 text-sm">: Behavioural, values, and motivation insight</span>
                  </a>
                  <a href="/leadership-mirror" className="flex items-center gap-3 bg-[#f0f5f8] rounded-lg p-4 border border-slate-100 hover:border-[#2693d0] transition-colors group">
                    <div className="w-2 h-2 bg-[#2693d0] rounded-full flex-shrink-0" />
                    <span className="text-slate-700 text-base font-medium group-hover:text-[#2693d0]">Leadership Mirror™</span>
                    <span className="text-slate-500 text-sm">: 360° feedback and self-perception gap</span>
                  </a>
                </div>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  This ensures that:
                </p>
                <ul className="space-y-4 mb-8">
                  {insightLedBenefits.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#2693d0] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-[#f0f5f8] rounded-xl p-6 border border-slate-100">
                  <p className="text-slate-800 font-medium text-base leading-relaxed mb-2">
                    Coaching is not used as a standalone solution.
                  </p>
                  <p className="text-slate-600 text-base leading-relaxed">
                    It is deployed deliberately to support behaviour change aligned with organisational priorities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Coaching Engagements */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-16 leading-tight">
              Coaching Engagements
            </h2>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Individual Coaching */}
              <div className="bg-white rounded-2xl p-8 lg:p-10 card-pulse-border">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#2693d0]/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-[#2693d0]" />
                  </div>
                  <h3 className="text-slate-800 font-semibold text-xl">
                    Individual executive and senior leader coaching
                  </h3>
                </div>
                
                <p className="text-slate-600 text-base mb-4">Used to support:</p>
                <ul className="space-y-2 mb-6">
                  {individualCoachingSupports.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2 flex-shrink-0" />
                      <span className="text-slate-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                
                <p className="text-slate-600 text-base mb-4">Coaching is:</p>
                <ul className="space-y-2">
                  {individualCoachingIs.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2 flex-shrink-0" />
                      <span className="text-slate-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Team Coaching */}
              <div className="bg-white rounded-2xl p-8 lg:p-10 card-pulse-border">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#2693d0]/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#2693d0]" />
                  </div>
                  <h3 className="text-slate-800 font-semibold text-xl">
                    Team and leadership group coaching
                  </h3>
                </div>
                
                <p className="text-slate-600 text-base mb-4">Used to support:</p>
                <ul className="space-y-2 mb-6">
                  {teamCoachingSupports.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2 flex-shrink-0" />
                      <span className="text-slate-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                
                <p className="text-slate-600 text-base mb-4">Team coaching is informed by:</p>
                <ul className="space-y-2">
                  {teamCoachingInformedBy.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2 flex-shrink-0" />
                      <span className="text-slate-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Leadership Development Programmes */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Leadership Development Programmes
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  RESILIGENT designs leadership development programmes that are:
                </p>
                <div className="grid grid-cols-2 gap-3 mb-8">
                  {programmeAttributes.map((item, index) => (
                    <div key={index} className="bg-[#f0f5f8] rounded-lg px-4 py-3 text-center">
                      <span className="text-slate-700 text-sm font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  Programmes may focus on:
                </p>
                <ul className="space-y-3 mb-8">
                  {programmeFocus.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-[#2693d0]/5 rounded-xl p-6 border border-[#2693d0]/20">
                  <p className="text-slate-800 font-medium text-lg">
                    Development is shaped by insight, not templates.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Impact AS Plus Coaching Portal */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <img 
                  src={impactASPlusLogo} 
                  alt="Impact AS Plus" 
                  className="h-12 lg:h-14 w-auto mb-8"
                />
                <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                  The Impact AS Plus™ Coaching Portal
                </h2>
                <p className="text-slate-200 text-base lg:text-lg leading-relaxed mb-6">
                  To support coaching at scale, RESILIGENT has built the Impact AS Plus™ Coaching Portal, a proprietary digital environment designed specifically for leadership coaching and development.
                </p>
                <div className="flex items-center gap-3">
                  <Laptop className="w-6 h-6 text-[#2693d0]" />
                  <p className="text-slate-300 text-base">
                    Purpose-built to support insight-led leadership development
                  </p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <p className="text-slate-300 font-medium mb-4">The portal provides:</p>
                  <ul className="space-y-3">
                    {portalFeatures.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-[#2693d0] flex-shrink-0 mt-0.5" />
                        <span className="text-slate-200 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <p className="text-slate-300 font-medium mb-4">This enables organisations to:</p>
                  <ul className="space-y-3">
                    {portalEnables.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2 flex-shrink-0" />
                        <span className="text-slate-200 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Integrating Proprietary and External Insight */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                Integrating Proprietary and External Insight
              </h2>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Coaching and leadership development are informed by insight drawn from:
              </p>
              
              <ul className="space-y-3 mb-8">
                {insightSources.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="bg-[#f0f5f8] rounded-xl p-6 border border-slate-100">
                <p className="text-slate-700 text-base leading-relaxed">
                  External tools, such as Hogan, Everything DISC®, and the Leadership Circle Profile™, are used selectively and complementarily, never as substitutes for contextual understanding or judgement.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What Organisations Gain */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  What Organisations Gain
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Through coaching and leadership development with RESILIGENT, organisations gain:
                </p>
              </div>
              
              <div>
                <ul className="space-y-4">
                  {organisationGains.map((item, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <CheckCircle className="w-5 h-5 text-[#2693d0] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* How Organisations Typically Begin */}
        <section className="bg-white py-20 lg:py-28 border-t border-slate-100">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                How Organisations Typically Begin
              </h2>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Most organisations begin with:
              </p>
              
              <ul className="space-y-3 mb-8">
                {typicalBeginnings.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                Coaching and development can then scale over time as part of a broader leadership or culture agenda.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-[#e8f2f7] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Next Steps
              </h2>
              
              {/* Primary CTA */}
              <div className="bg-white card-pulse-border rounded-xl p-8 mb-6 shadow-sm">
                <h3 className="text-slate-800 font-semibold text-xl mb-4">
                  Start a conversation
                </h3>
                <p className="text-slate-600 text-base leading-relaxed mb-6">
                  A confidential discussion to explore how coaching and leadership development can support your leaders and organisational priorities.
                </p>
                <a
                  href="mailto:info@resiligent.com?subject=Coaching%20%26%20Leadership%20Development%20Enquiry"
                  className="inline-flex items-center gap-2 bg-[#2693d0] text-white px-6 py-3.5 rounded font-medium text-base hover:bg-[#1e7ab8] transition-all duration-300"
                >
                  Start a Conversation
                  <ArrowRight size={18} />
                </a>
              </div>

              {/* Secondary CTA */}
              <a
                href="/impact-as-plus"
                className="inline-flex items-center gap-3 text-slate-600 hover:text-[#2693d0] transition-colors duration-300"
              >
                <FileText className="w-5 h-5" />
                <span className="text-base font-medium">Explore Impact AS Plus™</span>
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>

        {/* Footer Note */}
        <section className="bg-[#e8f2f7] pb-12 border-t border-slate-200">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-8">
            <p className="text-slate-500 text-sm">
              All coaching engagements are delivered by accredited coaches and governed by professional, ethical, and organisational standards.
            </p>
          </div>
        </section>
      </main>

      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default CoachingPage;
